import React, { useState, useEffect } from 'react';
import { FiPlus, FiCopy, FiTrash2, FiX, FiChevronDown, FiChevronUp, FiPrinter, FiEdit2 } from 'react-icons/fi';
import DashboardLayout from '../components/DashboardLayout';
import { fetchTasks, fetchEvents, createTask, deleteTask, updateTask } from '../apiService';
import './Tasks.css';

function Tasks() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState({});
  const [checkedTasks, setCheckedTasks] = useState({});
  const [formData, setFormData] = useState({
    taskName: '',
    description: '',
    dueDate: '',
    status: 'pending',
    assignedTo: '',
    eventId: '',
  });
  const [formError, setFormError] = useState('');
  const [editingTask, setEditingTask] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    if (!user.userId) return;
    setLoading(true);
    const [tasksResult, eventsResult] = await Promise.all([
      fetchTasks(user.userId),
      fetchEvents(user.userId),
    ]);
    if (tasksResult.success) setTasks(tasksResult.data);
    if (eventsResult.success) {
      setEvents(eventsResult.data);
      const expanded = {};
      eventsResult.data.forEach(e => { expanded[e.id] = true; });
      expanded['unassigned'] = true;
      setExpandedEvents(expanded);
    }
    setLoading(false);
  }

  function groupTasksByEvent() {
    const groups = {};
    tasks.forEach(task => {
      const key = task.eventId || 'unassigned';
      if (!groups[key]) {
        groups[key] = {
          eventId: task.eventId || null,
          eventName: task.eventName || 'Unassigned Tasks',
          tasks: [],
        };
      }
      groups[key].tasks.push(task);
    });
    return Object.values(groups);
  }

  function toggleEventExpanded(eventId) {
    setExpandedEvents(prev => ({
      ...prev,
      [eventId || 'unassigned']: !prev[eventId || 'unassigned'],
    }));
  }

  function toggleTaskChecked(taskId) {
    setCheckedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  }

  async function handleSubmitTask(e) {
    e.preventDefault();
    setFormError('');

    if (!formData.taskName) {
      setFormError('Task name is required.');
      return;
    }

    let result;
    if (editingTask) {
      result = await updateTask(editingTask.id, {
        taskName: formData.taskName,
        description: formData.description || null,
        dueDate: formData.dueDate || null,
        status: formData.status,
        assignedTo: formData.assignedTo || null,
      });
    } else {
      result = await createTask({
        userId: user.userId,
        taskName: formData.taskName,
        description: formData.description || null,
        dueDate: formData.dueDate || null,
        status: formData.status,
        assignedTo: formData.assignedTo || null,
        eventId: formData.eventId || null,
      });
    }

    if (result.success) {
      setShowForm(false);
      setEditingTask(null);
      setFormData({ taskName: '', description: '', dueDate: '', status: 'pending', assignedTo: '', eventId: '' });
      loadData();
    } else {
      setFormError(result.error || 'Failed to save task.');
    }
  }

  function handleInputChange(e) {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }
  async function handleDeleteTask(taskId) {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    const result = await deleteTask(taskId);
    if (result.success) {
      loadData();
    }
  }

  function handleEditClick(task) {
    setEditingTask(task);
    setFormData({
      taskName: task.taskName,
      description: task.description || '',
      dueDate: task.dueDate || '',
      status: task.status || 'pending',
      assignedTo: task.assignedTo || '',
      eventId: task.eventId || '',
    });
    setShowForm(true);
  }

  function getStatusClass(status) {
    if (!status) return 'status-pending';
    if (status === 'completed') return 'status-completed';
    if (status === 'in_progress') return 'status-in-progress';
    return 'status-pending';
  }

  function getStatusLabel(status) {
    if (!status) return 'Pending';
    if (status === 'completed') return 'Completed';
    if (status === 'in_progress') return 'In Progress';
    return 'Pending';
  }

  function getDueInInfo(dueDate) {
    if (!dueDate) return { label: '—', kind: 'none' };
    const now = new Date();
    const due = new Date(dueDate);
    const diffDays = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { label: 'Overdue', kind: 'overdue' };
    if (diffDays === 0) return { label: 'Today', kind: 'today' };
    if (diffDays === 1) return { label: '1 day', kind: 'soon' };
    if (diffDays <= 7) return { label: `${diffDays} days`, kind: 'soon' };
    const weeks = Math.ceil(diffDays / 7);
    if (diffDays <= 30) return { label: weeks === 1 ? '1 week' : `${weeks} weeks`, kind: 'later' };
    const months = Math.ceil(diffDays / 30);
    if (diffDays <= 365) return { label: months === 1 ? '1 month' : `${months} months`, kind: 'later' };
    const years = Math.ceil(diffDays / 365);
    return { label: years === 1 ? '1 year' : `${years} years`, kind: 'later' };
  }

  const taskGroups = groupTasksByEvent();

  return (
    <DashboardLayout>
      <div className="tasks-header">
        <div>
          <h1 className="page-title">Tasks</h1>
          <p className="tasks-breadcrumb">Tasks › All Tasks</p>
        </div>
        <button className="create-task-btn" onClick={() => setShowForm(true)}>
          <FiPlus size={18} />
          Add New Task
        </button>
      </div>

      {showForm && (
        <div className="task-form-overlay">
          <div className="task-form-card">
            <div className="form-header">
              <h2>{editingTask ? 'Edit Task' : 'Add New Task'}</h2>
              <button className="close-btn" onClick={() => { setShowForm(false); setEditingTask(null); }}>
                <FiX size={20} />
              </button>
            </div>
            {formError && <p className="form-error">{formError}</p>}
            <form onSubmit={handleSubmitTask}>
              <div className="form-group">
                <label>Task Name</label>
                <input type="text" name="taskName" value={formData.taskName} onChange={handleInputChange} placeholder="Enter task name" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea name="description" value={formData.description} onChange={handleInputChange} placeholder="Enter task description" rows="3" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Due Date</label>
                  <input type="date" name="dueDate" value={formData.dueDate} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleInputChange}>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Assigned To</label>
                  <input type="text" name="assignedTo" value={formData.assignedTo} onChange={handleInputChange} placeholder="Enter name" />
                </div>
                <div className="form-group">
                  <label>Event</label>
                  <select name="eventId" value={formData.eventId} onChange={handleInputChange}>
                    <option value="">No Event</option>
                    {events.map(event => (
                      <option key={event.id} value={event.id}>{event.eventName}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="submit-btn">{editingTask ? 'Save Changes' : 'Create Task'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <p className="loading-text">Loading tasks...</p>
      ) : taskGroups.length > 0 ? (
        taskGroups.map((group) => (
          <div className="task-group" key={group.eventId || 'unassigned'}>
            <div className="task-group-header" onClick={() => toggleEventExpanded(group.eventId)}>
              <div className="task-group-title">
                <span className="task-group-icon">☰</span>
                <span className="task-group-name">{group.eventName}</span>
                <span className="task-count">{group.tasks.length} tasks</span>
              </div>
              {expandedEvents[group.eventId || 'unassigned'] ? (
                <FiChevronUp size={18} />
              ) : (
                <FiChevronDown size={18} />
              )}
            </div>

            {expandedEvents[group.eventId || 'unassigned'] && (
              <div className="task-table-wrapper">
                <table className="task-table">
                  <thead>
                    <tr>
                      <th className="th-check"></th>
                      <th>Due In</th>
                      <th>Task Name</th>
                      <th>Assigned To</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.tasks.map((task) => (
                      <tr key={task.id} className={checkedTasks[task.id] ? 'checked' : ''}>
                        <td className="td-check">
                          <input
                            type="checkbox"
                            checked={checkedTasks[task.id] || false}
                            onChange={() => toggleTaskChecked(task.id)}
                          />
                        </td>
                        <td className="td-duration">
                          {(() => { const d = getDueInInfo(task.dueDate); return <span className={`duration-badge duration-${d.kind}`}>{d.label}</span>; })()}
                        </td>
                        <td className="td-task-name">
                          <p className="task-name">{task.taskName}</p>
                          {task.description && <p className="task-desc">{task.description}</p>}
                        </td>
                        <td className="td-assigned">
                          <span className="assigned-badge">{task.assignedTo || '—'}</span>
                        </td>
                        <td className="td-status">
                          <span className={`task-status-badge ${getStatusClass(task.status)}`}>
                            {getStatusLabel(task.status)}
                          </span>
                        </td>
                        <td className="td-actions">
                          <button className="task-action-btn" title="Copy"><FiCopy size={14} /></button>
                          <button className="task-action-btn" title="Edit" onClick={() => handleEditClick(task)}><FiEdit2 size={14} /></button>
                          <button className="task-action-btn delete" title="Delete" onClick={() => handleDeleteTask(task.id)}><FiTrash2 size={14} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))
      ) : (
        <p className="no-tasks">No tasks found.</p>
      )}

      <div className="tasks-footer-actions">
        <button className="print-btn" onClick={() => window.print()}>
          <FiPrinter size={16} />
          Print Checklist
        </button>
      </div>
    </DashboardLayout>
  );
}

export default Tasks;